import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, ListOrdered, Pencil, Play, Plus, Rows3, Trash2 } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import { groupModeLabel } from '@/lib/groups';
import { liveToggleState, useWatchLivePreference } from '@/lib/liveView';
import { useRunCollection } from '@/lib/runCollection';
import { RunCollectionPicker } from '@/components/testdata/RunCollectionPicker';
import { useRunBuild } from '@/lib/runBuild';
import { RunBuildPicker } from '@/components/builds/RunBuildPicker';
import { useRunDevice } from '@/lib/runDevice';
import { RunDevicePicker } from '@/components/builds/RunDevicePicker';
import type { Project, TestCase, TestGroup } from '@/lib/types';
import { WatchLiveToggle } from '@/components/report/WatchLiveToggle';
import { Badge, Button, Card, EmptyState, ErrorNotice, PageLoader, StatusPill, useConfirm, useToast } from '@/components/ui';
import { GroupDialog } from './GroupDialog';

/** The project's test groups: create, edit (creator or Project Admin) and run (anyone who runs tests). */
export function GroupsTab({ project, tests, canWrite, canRun }: { project: Project; tests: TestCase[]; canWrite: boolean; canRun: boolean }) {
  const { canModify } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<TestGroup | 'new' | null>(null);
  const [watchLive, setWatchLive] = useWatchLivePreference();
  const live = liveToggleState(project, watchLive);
  const runData = useRunCollection(project.id);
  const runBuild = useRunBuild(project.id, project.platform);
  const runDevice = useRunDevice(project.id, project.platform);
  const groups = useQuery({ queryKey: ['groups', project.id], queryFn: () => api.groups(project.id) });

  const run = useMutation({
    mutationFn: (g: TestGroup) => api.runGroup(g.id, { liveView: live.checked, collectionId: runData.collectionId, appBuildId: runBuild.appBuildId, deviceProfile: runDevice.deviceProfile }),
    onSuccess: (r, g) => {
      toast.success(`“${g.name}” started`);
      qc.invalidateQueries({ queryKey: ['groups', project.id] });
      qc.invalidateQueries({ queryKey: ['runs', project.id] });
      navigate(`/runs/${r.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (g: TestGroup) => api.deleteGroup(g.id),
    onSuccess: () => {
      toast.success('Group deleted');
      qc.invalidateQueries({ queryKey: ['groups', project.id] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (groups.isPending) return <PageLoader />;
  if (groups.isError) return <ErrorNotice error={groups.error} onRetry={() => groups.refetch()} />;

  const dialog = editing && (
    <GroupDialog
      key={editing === 'new' ? 'new' : editing.id}
      projectId={project.id}
      tests={tests}
      groups={groups.data}
      group={editing === 'new' ? undefined : editing}
      open
      onClose={() => setEditing(null)}
    />
  );

  if (groups.data.length === 0) {
    return (
      <>
        {dialog}
        <EmptyState
          icon={<Layers className="size-6" />}
          title="No test groups yet"
          description="Group tests that belong together - for example a smoke check or a checkout journey - and run them one after another or at the same time."
          action={
            canWrite && (
              <Button onClick={() => setEditing('new')} disabled={tests.length === 0} icon={<Plus className="size-4" />}>
                {tests.length === 0 ? 'Create a test first' : 'Create a group'}
              </Button>
            )
          }
        />
      </>
    );
  }

  return (
    <Card>
      {dialog}
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3.5 dark:border-slate-800">
        <p className="text-sm text-slate-500">
          {groups.data.length} group{groups.data.length === 1 ? '' : 's'}
        </p>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {canRun && <RunBuildPicker state={runBuild} />}
          {canRun && <RunDevicePicker state={runDevice} />}
          {canRun && <RunCollectionPicker state={runData} />}
          {canRun && <WatchLiveToggle state={live} onChange={setWatchLive} />}
          {canWrite && (
            <Button variant="outline" onClick={() => setEditing('new')} icon={<Plus className="size-4" />}>
              New group
            </Button>
          )}
        </div>
      </div>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {groups.data.map((g) => {
          const mine = canModify('test.write', g.createdById, project.orgId);
          return (
            <li key={g.id} className="flex flex-wrap items-center gap-4 px-5 py-3.5">
              <span className="grid size-9 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-300">
                {g.mode === 'PARALLEL' ? <Rows3 className="size-[18px]" /> : <ListOrdered className="size-[18px]" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-900 dark:text-white">{g.name}</div>
                <div className="truncate text-xs text-slate-500" title={g.tests.map((t) => t.name).join(', ')}>
                  {g.tests.length} test{g.tests.length === 1 ? '' : 's'}: {g.tests.map((t) => t.name).join(', ')}
                  {g.createdBy && ` · by ${g.createdBy.name}`}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge tone="brand">{groupModeLabel(g)}</Badge>
                {g.stopOnFailure && <Badge tone="warning">Stops at first failure</Badge>}
                {g.lastRun && (
                  <Link to={`/runs/${g.lastRun.id}`} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-600" title="Open the last run">
                    <StatusPill status={g.lastRun.status} /> {timeAgo(g.lastRun.finishedAt ?? g.lastRun.queuedAt)}
                  </Link>
                )}
              </div>
              <div className="flex gap-1">
                {canRun && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => run.mutate(g)}
                    loading={run.isPending && run.variables?.id === g.id}
                    disabled={g.tests.length === 0}
                    icon={<Play className="size-4" />}
                  >
                    Run group
                  </Button>
                )}
                {mine && (
                  <>
                    <Button size="sm" variant="ghost" aria-label={`Edit ${g.name}`} onClick={() => setEditing(g)} icon={<Pencil className="size-4" />} />
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Delete ${g.name}`}
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Delete this group?',
                          message: `“${g.name}” will be deleted. Its tests and past reports stay.`,
                          confirmLabel: 'Delete group',
                        });
                        if (ok) remove.mutate(g);
                      }}
                      icon={<Trash2 className="size-4 text-rose-500" />}
                    />
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
